"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashboardManager = void 0;
const lodash_1 = require("lodash");
const GlobalLibraryRepository_1 = require("./GlobalLibraryRepository");
const VizzlyApiClientLogger_1 = require("./VizzlyApiClientLogger");
class DashboardManager {
    constructor(vizzly) {
        this.vizzly = vizzly;
    }
    create(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const dashboardRepository = this.vizzly.getDashboardRepository();
            return yield dashboardRepository.createDashboard(params);
        });
    }
    update(params) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const dashboardRepository = this.vizzly.getDashboardRepository();
            const globalLibraryRepository = this.vizzly.getGlobalLibraryRepository();
            const [{ global_libraries: globalLibraries, permissions: globalLibraryPermissions }, { dashboards, permissions: dashboardPermissions },] = yield Promise.all([
                globalLibraryRepository.fetchGlobalLibraries(params),
                dashboardRepository.fetchDashboards(params),
            ]);
            const dashboardToUpdate = dashboards.find((dashboard) => dashboard.id === params.dashboardId);
            if (!dashboardToUpdate)
                throw new Error('Cannot find dashboard to update.');
            const { parentGlobalLibraries, childGlobalLibraries } = GlobalLibraryRepository_1.GlobalLibraryRepository.splitParentAndChildGlobalLibraries(globalLibraries);
            // Get the token to update the dashboard.
            const updateDashboardToken = dashboardPermissions.find((permission) => permission.scope === 'read_write' && permission.dashboardId === params.dashboardId);
            if (!updateDashboardToken)
                throw new Error(`Failed to find update token for dashboard.`);
            // Send the update request.
            const updatedDashboard = yield dashboardRepository.updateDashboard(Object.assign(Object.assign({}, params), { dashboardPermission: updateDashboardToken }));
            // Build the views to store in the libraries
            let libraryViewsToSave = [];
            if ((_a = params.definition) === null || _a === void 0 ? void 0 : _a.componentLibrary) {
                libraryViewsToSave = (0, lodash_1.cloneDeep)(params.definition.componentLibrary || []).filter((c) => {
                    const isParentDashboard = dashboardToUpdate.parent_dashboard_id === null;
                    if (isParentDashboard) {
                        // All content of the library are for the parent library, if the parent dashboard
                        // is being edited.
                        return true;
                    }
                    else {
                        return !c.attributes.protectedByOrganisation;
                    }
                });
            }
            const librariesToUpdate = dashboardToUpdate.parent_dashboard_id === null ? parentGlobalLibraries : childGlobalLibraries;
            // If it's not a parent dashboard, and there are views or custom fields to save, but there are no libraries
            // then create one first.
            const isParent = dashboardToUpdate.parent_dashboard_id === null;
            const hasThingsToSaveInLibrary = libraryViewsToSave.length > 0 || !!((_b = params.definition) === null || _b === void 0 ? void 0 : _b.customFields);
            const parentGlobalLibrary = globalLibraries.find((gL) => gL.parent_global_library_id === null);
            if (!isParent && parentGlobalLibrary && hasThingsToSaveInLibrary && librariesToUpdate.length === 0) {
                yield globalLibraryRepository.createGlobalLibrary({
                    library: {
                        views: libraryViewsToSave,
                        // No concept of being protected, so only option is to save them all
                        customFields: ((_c = params.definition) === null || _c === void 0 ? void 0 : _c.customFields) || {},
                    },
                    parentGlobalLibraryId: parentGlobalLibrary.id,
                    permissions: [{ scope: 'read_write' }],
                });
            }
            const updatePromises = librariesToUpdate.map((gL) => __awaiter(this, void 0, void 0, function* () {
                var _d;
                const writePermission = globalLibraryPermissions.find((permission) => permission.globalLibraryId === gL.id && permission.scope === 'read_write');
                if (!writePermission)
                    VizzlyApiClientLogger_1.VizzlyApiClientLogger.error('No write permission found.');
                if (writePermission) {
                    yield globalLibraryRepository.updateGlobalLibrary({
                        globalLibrarySessionToken: writePermission.token,
                        library: {
                            views: libraryViewsToSave,
                            // No concept of being protected, so only option is to save them all
                            customFields: ((_d = params.definition) === null || _d === void 0 ? void 0 : _d.customFields) || {},
                        },
                    });
                }
            }));
            yield Promise.all(updatePromises);
            return updatedDashboard;
        });
    }
    getDashboards(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const dashboardRepo = this.vizzly.getDashboardRepository();
            const libraryRepo = this.vizzly.getGlobalLibraryRepository();
            const [{ dashboards }, { global_libraries: globalLibraries }] = yield Promise.all([
                dashboardRepo.fetchDashboards(params),
                libraryRepo.fetchGlobalLibraries(params),
            ]);
            return DashboardManager.mergeDashboardsAndGlobalLibraries(dashboards, globalLibraries);
        });
    }
    static mergeDashboardsAndGlobalLibraries(dashboards, globalLibraries) {
        return (0, lodash_1.cloneDeep)(dashboards).map((dashboard) => {
            // Views attached directly to a library on the parent dashboard.
            let dashboardComponentLibrary = (0, lodash_1.cloneDeep)(dashboard.definition.componentLibrary);
            let parentDashboardComponentLibrary = dashboard.parent_dashboard_id === null
                ? []
                : dashboards.find((d) => d.id === dashboard.parent_dashboard_id).definition.componentLibrary;
            let { parentGlobalLibraries, childGlobalLibraries } = GlobalLibraryRepository_1.GlobalLibraryRepository.splitParentAndChildGlobalLibraries(globalLibraries);
            // Only include the child libraries if the dashboard is not a parent.
            childGlobalLibraries = dashboard.parent_dashboard_id === null ? [] : childGlobalLibraries;
            // Add the views from the various sources together. The order does matter -
            // first elements are kept if a duplicate view is found.
            let newLibrary = (0, lodash_1.uniqBy)([
                ...DashboardManager.markViewsAsProtected(parentGlobalLibraries.flatMap((gL) => gL.library.views || [])),
                ...childGlobalLibraries.flatMap((gL) => gL.library.views || []),
                ...DashboardManager.markViewsAsProtected(parentDashboardComponentLibrary),
                ...(dashboard.parent_dashboard_id === null
                    ? DashboardManager.markViewsAsProtected(dashboardComponentLibrary)
                    : dashboardComponentLibrary || []),
            ], (view) => view.attributes.viewId);
            dashboard.definition.componentLibrary = newLibrary;
            dashboard.definition.customFields = DashboardManager.mergeCustomFields(dashboard, dashboards, parentGlobalLibraries, childGlobalLibraries);
            return dashboard;
        });
    }
    static mergeCustomFields(dashboard, dashboards, parentGlobalLibrariesForDashboard, childGlobalLibrariesForDashboard) {
        var _a;
        const maybeParentDashboardCustomFields = dashboard.parent_dashboard_id === null
            ? {}
            : ((_a = dashboards.find((d) => d.id === dashboard.parent_dashboard_id)) === null || _a === void 0 ? void 0 : _a.definition.customFields) || {};
        const doMerge = (...toBeMerged) => {
            return toBeMerged.reduce((customFields, acc) => {
                Object.keys(customFields || {}).forEach((dataSetId) => {
                    if (!acc[dataSetId])
                        acc[dataSetId] = [];
                    acc[dataSetId] = (0, lodash_1.uniqBy)([...acc[dataSetId], ...((customFields === null || customFields === void 0 ? void 0 : customFields[dataSetId]) || [])], (cF) => cF.id);
                });
                return acc;
            }, {});
        };
        return doMerge((0, lodash_1.cloneDeep)(dashboard.definition.customFields) || {}, (0, lodash_1.cloneDeep)(maybeParentDashboardCustomFields) || {}, ...(0, lodash_1.cloneDeep)(childGlobalLibrariesForDashboard).map((p) => p.library.customFields || {}), ...(0, lodash_1.cloneDeep)(parentGlobalLibrariesForDashboard).map((p) => p.library.customFields || {}));
    }
    static markViewsAsProtected(componentLibrary) {
        return (componentLibrary || []).map((view) => (Object.assign(Object.assign({}, view), { attributes: Object.assign(Object.assign({}, view.attributes), { protectedByOrganisation: true }) })));
    }
}
exports.DashboardManager = DashboardManager;
